/** Minimal RIFF/WAVE reader for PCM 16-bit and IEEE float 32-bit; returns mono float samples. */
export interface DecodedWav {
  sampleRate: number;
  samples: Float32Array;
}

export function decodeWav(bytes: ArrayBuffer | Uint8Array): DecodedWav {
  const buffer = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const view = new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength);
  const tag = (offset: number) =>
    String.fromCharCode(...Array.from(buffer.subarray(offset, offset + 4)));
  if (tag(0) !== 'RIFF' || tag(8) !== 'WAVE') throw new Error('not a WAVE file');

  let format = 0;
  let channels = 1;
  let sampleRate = 0;
  let bits = 0;
  let offset = 12;
  while (offset + 8 <= buffer.length) {
    const id = tag(offset);
    const size = view.getUint32(offset + 4, true);
    const body = offset + 8;
    if (id === 'fmt ') {
      format = view.getUint16(body, true);
      channels = view.getUint16(body + 2, true);
      sampleRate = view.getUint32(body + 4, true);
      bits = view.getUint16(body + 14, true);
      if (format === 0xfffe) format = view.getUint16(body + 24, true); // WAVE_FORMAT_EXTENSIBLE
    } else if (id === 'data') {
      const bytesPerSample = bits / 8;
      const frames = Math.floor(Math.min(size, buffer.length - body) / (bytesPerSample * channels));
      const samples = new Float32Array(frames);
      for (let i = 0; i < frames; i++) {
        let sum = 0;
        for (let c = 0; c < channels; c++) {
          const at = body + (i * channels + c) * bytesPerSample;
          if (format === 1 && bits === 16) sum += view.getInt16(at, true) / 32768;
          else if (format === 3 && bits === 32) sum += view.getFloat32(at, true);
          else throw new Error(`unsupported WAV format ${format}/${bits}-bit`);
        }
        samples[i] = sum / channels;
      }
      return { sampleRate, samples };
    }
    offset = body + size + (size % 2);
  }
  throw new Error('WAVE file has no data chunk');
}
